import { useState } from 'react';
import { Card, Badge, DataTable, SlidePanel, PanelSection, SpecGrid, SpecCell, ModuleRow, AuditRow, Modal, Toggle, FormGroup, FormInput, FormSelect, toast, type Column } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { exportCsv } from '@/utils/csv-export';
import { MODULE_DEFINITIONS, SUPPORT, TRIAL_DAYS } from '@mirai/shared';
import { useRole } from '@/hooks/useRole';
import styles from './Tenants.module.css';

const formatCurrency = (v: number) => `¥${v.toLocaleString('ja-JP')}`;

function formatLoginTime(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffH = diffMs / 3600000;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (diffH < 24 && d.getDate() === now.getDate()) return `今日 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (diffH < 48) return `昨日 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Tenant {
  id: string; slug: string; company_name: string; license_number: string; office_address?: string; brand_color: string;
  plan_name: string; price_monthly: number; status: string; user_count: number; property_count: number;
  storage_used_gb: number; max_users?: number; max_properties?: number; max_storage_gb?: number;
  modules?: string[]; created_at: string; last_login_at: string | null;
  custom_domain?: string | null; custom_domain_ssl?: boolean; custom_domain_verified_at?: string | null;
  trial_started_at?: string | null; trial_expires_at?: string | null;
}

function getTrialDaysLeft(tenant: Tenant): number | null {
  if (tenant.status !== 'trial' || !tenant.trial_expires_at) return null;
  const diff = new Date(tenant.trial_expires_at).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
}

const statusLabel: Record<string, string> = { active: 'アクティブ', trial: 'トライアル', unpaid: '未払い', suspended: '停止中', cancelling: '解約申請中' };
const statusVariant: Record<string, 'green' | 'blue' | 'amber' | 'red' | 'gray'> = { active: 'green', trial: 'blue', unpaid: 'amber', suspended: 'red', cancelling: 'gray' };
const planBadgeVariant: Record<string, 'blue' | 'green' | 'purple' | 'amber'> = { Trial: 'blue', Standard: 'blue', Professional: 'green', Enterprise: 'amber' };

const moduleList = MODULE_DEFINITIONS;

interface AuditLog {
  id: string;
  user_name: string;
  action: string;
  description: string;
  ip_address: string | null;
  created_at: string;
}

export function Tenants() {
  const { isSuperAdmin, isAdmin } = useRole();
  const { data: tenants, loading, refetch } = useApi<Tenant[]>('/console/tenants');
  const [selected, setSelected] = useState<Tenant | null>(null);
  const [tenantEditMode, setTenantEditMode] = useState(false);
  const [tenantEditForm, setTenantEditForm] = useState({ company_name: '', license_number: '', office_address: '', brand_color: '', tel: '' });

  const columns: Column<Tenant>[] = [
    { key: 'company_name', label: 'テナント', render: (r) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div className={styles.tAv} style={{ background: `${r.brand_color}22`, color: r.brand_color, border: `1px solid ${r.brand_color}33` }}>
          {r.company_name.charAt(0)}
        </div>
        <div><div className={styles.tdMain}>{r.company_name}</div><div className={styles.tdSub}>{r.slug}.jiayin.jp</div></div>
      </div>
    )},
    { key: 'plan_name', label: 'プラン', render: (r) => <Badge variant={planBadgeVariant[r.plan_name] ?? 'blue'}>{r.plan_name}</Badge> },
    { key: 'user_count', label: 'ユーザー', render: (r) => <span style={{ fontFamily: 'var(--mono)' }}>{r.user_count} / {r.max_users ?? 3}</span> },
    { key: 'property_count', label: '物件数', render: (r) => <span style={{ fontFamily: 'var(--mono)' }}>{r.property_count}</span> },
    { key: 'price_monthly', label: '月次請求', render: (r) => <span style={{ fontFamily: 'var(--mono)' }}>{formatCurrency(r.price_monthly ?? 0)}</span> },
    { key: 'status', label: 'ステータス', render: (r) => {
      const daysLeft = getTrialDaysLeft(r);
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className={`${styles.pill} ${styles[`pill_${statusVariant[r.status] ?? 'gray'}`]}`}>{statusLabel[r.status] ?? r.status}</span>
          {daysLeft !== null && (
            <span style={{ fontSize: 10.5, color: daysLeft <= 3 ? 'var(--red, #e53935)' : daysLeft <= 7 ? 'var(--amber, #f59e0b)' : 'var(--ink3)', fontWeight: 600 }}>
              残{daysLeft}日
            </span>
          )}
        </div>
      );
    }},
    { key: 'last_login_at', label: '最終ログイン', render: (r) => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink2)' }}>{formatLoginTime(r.last_login_at)}</span> },
    { key: 'id', label: '', render: (r) => <button className={styles.detailBtn} onClick={(e) => { e.stopPropagation(); openDetail(r); }}>詳細</button> },
  ];
  const [planFilter, setPlanFilter] = useState('全プラン');
  const [statusFilter, setStatusFilter] = useState('全ステータス');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const planNameToId = (name: string) => name === 'Trial' ? '1' : name === 'Standard' ? '2' : name === 'Professional' ? '3' : '4';
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [customDomainInput, setCustomDomainInput] = useState('');
  const [customDomainLoading, setCustomDomainLoading] = useState(false);
  const [certInfo, setCertInfo] = useState<{ issuer: string; notAfter: string; daysRemaining: number; status: string } | null>(null);
  const [certLoading, setCertLoading] = useState(false);
  const [sslRenewing, setSslRenewing] = useState(false);
  const [subdomainSslLoading, setSubdomainSslLoading] = useState(false);
  const [showExtendTrial, setShowExtendTrial] = useState(false);
  const [extendDays, setExtendDays] = useState('7');
  const [extending, setExtending] = useState(false);

  const openDetail = async (t: Tenant) => {
    setSelected(t);
    setSelectedPlanId(planNameToId(t.plan_name));
    setEnabledModules(t.modules ?? ['properties', 'crm', 'contracts']);
    setCustomDomainInput(t.custom_domain ?? '');
    setCertInfo(null);
    try {
      const logs = await api<AuditLog[]>(`/console/tenants/${t.id}/audit-logs`);
      setAuditLogs(logs);
    } catch { setAuditLogs([]); }
    // SSL証明書情報を取得
    if (t.custom_domain && t.custom_domain_ssl) {
      setCertLoading(true);
      try {
        const info = await api<{ issuer: string; notAfter: string; daysRemaining: number; status: string }>(
          `/console/certificates/${encodeURIComponent(t.custom_domain)}`
        );
        setCertInfo(info);
      } catch { setCertInfo(null); }
      finally { setCertLoading(false); }
    }
  };
  const handleExtendTrial = async () => {
    if (!selected) return;
    const days = Number(extendDays);
    if (!days || days < 1 || days > 365) { toast('1〜365の範囲で入力してください', 'error'); return; }
    setExtending(true);
    try {
      await api(`/console/tenants/${selected.id}/extend-trial`, {
        method: 'POST',
        body: JSON.stringify({ days }),
      });
      toast(`トライアルを${days}日間延長しました`);
      setShowExtendTrial(false);
      refetch();
    } catch { toast('延長に失敗しました', 'error'); }
    finally { setExtending(false); }
  };

  const handleToggleModule = (modId: string) => {
    setEnabledModules((prev) => prev.includes(modId) ? prev.filter((m) => m !== modId) : [...prev, modId]);
    toast(`モジュール設定を変更しました`);
  };
  const [nt, setNt] = useState({ company_name: '', slug: '', license_number: '', tel: '', admin_email: '', plan_id: '2', billing_start: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10), modules: ['properties', 'crm', 'contracts', 'portals'] as string[] });
  const [verifyStep, setVerifyStep] = useState<'form' | 'code'>('form');
  const [verifyCode, setVerifyCode] = useState('');
  const [sending, setSending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const filtered = !tenants ? [] : tenants.filter((t) => {
    if (planFilter !== '全プラン' && t.plan_name !== planFilter) return false;
    if (statusFilter !== '全ステータス' && (statusLabel[t.status] ?? t.status) !== statusFilter) return false;
    return true;
  });

  const toggleModule = (mod: string) => {
    setNt((prev) => ({ ...prev, modules: prev.modules.includes(mod) ? prev.modules.filter((m) => m !== mod) : [...prev.modules, mod] }));
  };
  const startResendCooldown = () => {
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown((prev) => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
    }, 1000);
  };

  const handleSendVerification = async () => {
    if (!nt.company_name.trim()) { toast('会社名を入力してください', 'error'); return; }
    if (!nt.slug.trim()) { toast('スラッグを入力してください', 'error'); return; }
    if (!nt.admin_email.trim()) { toast('管理者メールアドレスを入力してください', 'error'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nt.admin_email)) { toast('メールアドレスの形式が正しくありません', 'error'); return; }
    setSending(true);
    try {
      const res = await api('/console/email-verify/send', {
        method: 'POST',
        body: JSON.stringify({ email: nt.admin_email }),
      }) as { error?: string };
      if (res.error) { toast(res.error, 'error'); return; }
      toast(`✓ ${nt.admin_email} に認証コードを送信しました`);
      setVerifyStep('code');
      startResendCooldown();
    } catch { toast('認証コードの送信に失敗しました', 'error'); }
    finally { setSending(false); }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setSending(true);
    try {
      const res = await api('/console/email-verify/send', {
        method: 'POST',
        body: JSON.stringify({ email: nt.admin_email }),
      }) as { error?: string };
      if (res.error) { toast(res.error, 'error'); return; }
      toast(`✓ 認証コードを再送信しました`);
      setVerifyCode('');
      startResendCooldown();
    } catch { toast('再送信に失敗しました', 'error'); }
    finally { setSending(false); }
  };

  const handleVerifyAndCreate = async () => {
    if (verifyCode.length !== 6) { toast('6桁の認証コードを入力してください', 'error'); return; }
    setSending(true);
    try {
      const confirmRes = await api('/console/email-verify/confirm', {
        method: 'POST',
        body: JSON.stringify({ email: nt.admin_email, code: verifyCode }),
      }) as { error?: string; verified?: boolean };
      if (confirmRes.error) { toast(confirmRes.error, 'error'); return; }
      await handleAddTenant();
    } catch { toast('認証に失敗しました', 'error'); }
    finally { setSending(false); }
  };

  const handleAddTenant = async () => {
    if (!nt.company_name || !nt.slug) {
      toast('会社名とスラッグは必須です', 'error');
      return;
    }
    try {
      if (!nt.slug.trim()) { toast('スラッグを入力してください', 'error'); return; }
      if (!nt.company_name.trim()) { toast('会社名を入力してください', 'error'); return; }
      const res = await api('/console/tenants', {
        method: 'POST',
        body: JSON.stringify({
          company_name: nt.company_name,
          slug: nt.slug,
          plan_id: nt.plan_id,
          license_number: nt.license_number || undefined,
          admin_email: nt.admin_email,
          tel: nt.tel || undefined,
          billing_start: nt.billing_start || undefined,
          modules: nt.modules,
        }),
      }) as { error?: string };
      if (res.error) { toast(res.error, 'error'); return; }
      toast('✓ テナントを作成しました。招待メールを送付しました');
      setShowAddModal(false);
      setVerifyStep('form');
      setVerifyCode('');
      setNt({ company_name: '', slug: '', license_number: '', tel: '', admin_email: '', plan_id: '2', billing_start: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10), modules: ['properties', 'crm', 'contracts', 'portals'] });
      refetch();
    } catch (e) { toast(`作成に失敗しました: ${e instanceof Error ? e.message : '不明なエラー'}`, 'error'); }
  };

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <FormSelect className={styles.filterSelect} value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
          <option>全プラン</option>
          <option>Standard</option>
          <option>Professional</option>
          <option>Enterprise</option>
          <option>Trial</option>
        </FormSelect>
        <FormSelect className={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option>全ステータス</option>
          <option>アクティブ</option>
          <option>トライアル</option>
          <option>未払い</option>
          <option>停止中</option>
          <option>解約申請中</option>
        </FormSelect>
        <div className={styles.toolbarRight}>
          <button className={styles.btnSecondary} onClick={() => {
            if (!filtered.length) { toast('エクスポートするデータがありません', 'error'); return; }
            const now = new Date();
            const dateStr = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`;
            exportCsv(`tenants_${dateStr}.csv`, [
              { key: 'company_name', label: '会社名' },
              { key: 'slug', label: 'スラッグ' },
              { key: 'plan_name', label: 'プラン' },
              { key: 'status', label: 'ステータス', format: (r: Record<string, unknown>) => statusLabel[r.status as string] ?? String(r.status) },
              { key: 'user_count', label: 'ユーザー数' },
              { key: 'property_count', label: '物件数' },
              { key: 'price_monthly', label: '月額（税抜）', format: (r: Record<string, unknown>) => String(r.price_monthly) },
              { key: 'storage_used_gb', label: 'ストレージ使用量(GB)' },
              { key: 'license_number', label: '宅建業免許番号' },
              { key: 'brand_color', label: 'ブランドカラー' },
              { key: 'created_at', label: '登録日', format: (r: Record<string, unknown>) => r.created_at ? new Date(r.created_at as string).toLocaleDateString('ja-JP') : '' },
            ], filtered as unknown as Record<string, unknown>[]);
            toast(`✓ ${filtered.length}件のテナントデータをエクスポートしました`);
          }}>📤 エクスポート</button>
          {isAdmin && <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>＋ テナント追加</button>}
        </div>
      </div>

      <Card title={`テナント一覧（${filtered.length}件）`}>
        {loading ? <div style={{ padding: 20, color: 'var(--ink3)' }}>読込中...</div> : (
          <DataTable columns={columns} rows={filtered} keyField="id" onRowClick={openDetail} />
        )}
      </Card>

      {/* テナント詳細パネル（旧HTMLモック dp-panel 完全移植） */}
      <SlidePanel open={!!selected} onClose={() => { setSelected(null); setTenantEditMode(false); }} title={selected?.company_name ?? 'テナント詳細'}
        actions={selected ? <>
          {isAdmin && !tenantEditMode && <button className={styles.btnSm} onClick={() => {
            setTenantEditForm({
              company_name: selected.company_name || '',
              license_number: selected.license_number || '',
              office_address: selected.office_address || '',
              brand_color: selected.brand_color || '#c0392b',
              tel: (selected as Record<string, unknown>).tel as string || '',
            });
            setTenantEditMode(true);
          }}>✏️ 編集</button>}
          {isAdmin && <button className={styles.btnSm} onClick={async () => {
            try {
              const res = await api<{ accessToken: string; user: { id: string; name: string; email: string; role: string }; tenant: { id: string; slug: string; companyName: string } }>(`/console/tenants/${selected!.id}/impersonate`, { method: 'POST' });
              const tenantBase = import.meta.env.VITE_USE_MOCKS === 'true' ? '/mock/tenant' : '/tenant';
              const tenantUrl = `${window.location.origin}${tenantBase}/dashboard`;
              const w = window.open('about:blank', '_blank');
              if (w) {
                w.localStorage.setItem('mirai_token', res.accessToken);
                w.localStorage.setItem('mirai_user', JSON.stringify({ ...res.user, tenantId: res.tenant.id, tenantSlug: res.tenant.slug }));
                w.location.href = tenantUrl;
              }
              toast(`${selected!.company_name} に代理ログインしました`);
            } catch { toast('代理ログインに失敗しました', 'error'); }
          }}>🔑 代理ログイン</button>}
          {isAdmin && <button className={styles.btnDanger} onClick={async () => {
            const newStatus = selected.status === 'suspended' ? 'active' : 'suspended';
            try {
              await api(`/console/tenants/${selected.id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
              toast(newStatus === 'suspended' ? 'アカウントを一時停止しました' : 'アカウントを再開しました');
              refetch();
              setSelected(null);
            } catch { toast('ステータス変更に失敗しました', 'error'); }
          }}>{selected.status === 'suspended' ? '▶ 再開' : '⏸ 停止'}</button>}
        </> : undefined}
      >
        {selected && <>
          {/* ヘッダー情報 */}
          <PanelSection>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: `${selected.brand_color}22`, color: selected.brand_color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, border: `1px solid ${selected.brand_color}33` }}>
                {selected.company_name.charAt(0)}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700 }}>{selected.company_name}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink2)', marginTop: 3 }}>{selected.slug}.jiayin.jp ｜ <span style={{ fontFamily: 'var(--mono)' }}>tenant_id: ten_{selected.slug}_001</span></div>
              </div>
              <span className={`${styles.pill} ${styles[`pill_${statusVariant[selected.status] ?? 'gray'}`]}`} style={{ marginLeft: 'auto' }}>{statusLabel[selected.status] ?? selected.status}</span>
            </div>
            <SpecGrid>
              <SpecCell label="プラン" value={selected.plan_name} />
              <SpecCell label="月次請求" value={formatCurrency(selected.price_monthly ?? 0)} mono />
              <SpecCell label="登録日" value={new Date(selected.created_at).toLocaleDateString('ja-JP')} mono />
              {(selected.status === 'trial' || selected.status === 'suspended') && selected.trial_expires_at && (
                <SpecCell label="トライアル期限">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--mono)', color: (getTrialDaysLeft(selected) ?? 99) <= 3 ? 'var(--red, #e53935)' : undefined }}>
                      {new Date(selected.trial_expires_at).toLocaleDateString('ja-JP')}
                      {selected.status === 'trial' ? `（残${getTrialDaysLeft(selected) ?? 0}日）` : '（期限切れ）'}
                    </span>
                    {isAdmin && (
                      <button className={styles.btnSm} onClick={() => { setExtendDays('7'); setShowExtendTrial(true); }}>
                        延長
                      </button>
                    )}
                  </div>
                </SpecCell>
              )}
              <SpecCell label="次回請求" value="2026/05/01" mono />
              <SpecCell label="ユーザー数" value={`${selected.user_count} / ${selected.max_users ?? 3}`} mono />
              <SpecCell label="物件数" value={String(selected.property_count)} mono />
              <SpecCell label="DB スキーマ" value={`tenant_${selected.slug}`} mono />
              <SpecCell label="ストレージ" value={`${selected.storage_used_gb} GB / ${selected.max_storage_gb ?? 50} GB`} mono />
              <SpecCell label="宅建業免許" value={selected.license_number || '—'} />
              <SpecCell label="主たる事務所" value={selected.office_address || '—'} />
            </SpecGrid>
          </PanelSection>

          {/* テナント情報編集 */}
          {tenantEditMode && (
            <PanelSection title="テナント情報編集">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className={styles.formGroup}><label className={styles.formLabel}>会社名</label><input className={styles.formInput} value={tenantEditForm.company_name} onChange={e => setTenantEditForm({ ...tenantEditForm, company_name: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>宅建業免許番号</label><input className={styles.formInput} value={tenantEditForm.license_number} onChange={e => setTenantEditForm({ ...tenantEditForm, license_number: e.target.value })} /></div>
                <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}><label className={styles.formLabel}>主たる事務所</label><input className={styles.formInput} value={tenantEditForm.office_address} onChange={e => setTenantEditForm({ ...tenantEditForm, office_address: e.target.value })} placeholder="東京都渋谷区..." /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>電話番号</label><input className={styles.formInput} value={tenantEditForm.tel} onChange={e => setTenantEditForm({ ...tenantEditForm, tel: e.target.value })} /></div>
                <div className={styles.formGroup}><label className={styles.formLabel}>ブランドカラー</label><input className={styles.formInput} type="color" value={tenantEditForm.brand_color} onChange={e => setTenantEditForm({ ...tenantEditForm, brand_color: e.target.value })} style={{ height: 36, padding: 2 }} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                <button className={styles.btnSm} onClick={() => setTenantEditMode(false)}>キャンセル</button>
                <button className={styles.btnSm} style={{ background: 'var(--brand)', color: '#fff' }} onClick={async () => {
                  try {
                    await api(`/console/tenants/${selected.id}`, { method: 'PATCH', body: JSON.stringify({
                      company_name: tenantEditForm.company_name,
                      license_number: tenantEditForm.license_number,
                      office_address: tenantEditForm.office_address,
                      brand_color: tenantEditForm.brand_color,
                      tel: tenantEditForm.tel,
                    })});
                    toast('テナント情報を更新しました');
                    setTenantEditMode(false);
                    setSelected(null);
                    refetch();
                  } catch { toast('更新に失敗しました', 'error'); }
                }}>保存</button>
              </div>
            </PanelSection>
          )}

          {/* モジュール制御 */}
          <PanelSection title="有効モジュール（このテナント個別制御）">
            {moduleList.map((m) => (
              <ModuleRow key={m.id} icon={m.icon} name={m.name} desc={m.desc}>
                <Toggle
                  checked={m.required || enabledModules.includes(m.id)}
                  onChange={() => handleToggleModule(m.id)}
                  disabled={m.required}
                />
              </ModuleRow>
            ))}
          </PanelSection>

          {/* プラン変更 */}
          <PanelSection title="プラン・課金変更">
            <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 9 }}>
              <FormSelect style={{ flex: 1 }} value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)}>
                <option value="1">{selectedPlanId === '1' ? 'トライアル（現在 無料）' : 'トライアル（無料）'}</option>
                <option value="2">{selectedPlanId === '2' ? 'Standard（現在 ¥25,000/月）' : 'Standard（¥25,000/月）'}</option>
                <option value="3">{selectedPlanId === '3' ? 'Professional（現在 ¥48,000/月）' : 'Professional（¥48,000/月）'}</option>
                <option value="4">{selectedPlanId === '4' ? 'Enterprise（現在 ¥98,000/月〜）' : 'Enterprise（¥98,000/月〜）'}</option>
              </FormSelect>
              <button className={styles.btnPrimary} onClick={async () => {
                try {
                  await api(`/console/tenants/${selected.id}`, { method: 'PATCH', body: JSON.stringify({ plan_id: Number(selectedPlanId) }) });
                  toast('✓ プランを変更しました。次回請求から適用されます');
                  setSelected(null);
                  refetch();
                } catch (e) { toast(e instanceof Error ? e.message : 'プラン変更に失敗しました', 'error'); }
              }}>変更を適用</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink3)' }}>※ アップグレード時は差額を日割り計算して当月請求に加算します。</div>
          </PanelSection>

          {/* 監査ログ */}
          <PanelSection title="監査ログ（直近）">
            {auditLogs.length > 0 ? auditLogs.map((log) => {
              const d = new Date(log.created_at);
              const time = `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}\n${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
              return <AuditRow key={log.id} time={time} event={`${log.action} — <strong>${log.user_name}</strong>: ${log.description ?? ''}`} ip={log.ip_address ?? '—'} />;
            }) : <div style={{ padding: 12, color: 'var(--ink3)', fontSize: 13 }}>監査ログなし</div>}
          </PanelSection>

          {/* サブドメイン SSL */}
          <PanelSection title="サブドメイン SSL">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <code style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--ink1)', background: 'var(--surface2)', padding: '6px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
                {selected.slug}.jiayin.jp
              </code>
              {isAdmin && (
                <button
                  className={styles.btnPrimary}
                  disabled={subdomainSslLoading}
                  onClick={async () => {
                    setSubdomainSslLoading(true);
                    try {
                      const r = await api<{ success: boolean; output: string }>(`/console/tenants/${selected.id}/subdomain-ssl`, { method: 'POST' });
                      if (r.success) toast('✓ SSL証明書を取得しました');
                      else toast('SSL取得に失敗しました: ' + r.output, 'error');
                    } catch (e) { toast(e instanceof Error ? e.message : 'SSL取得に失敗しました', 'error'); }
                    finally { setSubdomainSslLoading(false); }
                  }}
                >
                  {subdomainSslLoading ? '取得中（最大2分）...' : '🔒 SSL証明書を取得'}
                </button>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink3)' }}>証明書の取得には最大2分かかります。取得後はHTTPSでアクセスできます。</div>
          </PanelSection>

          {/* カスタムドメイン */}
          <PanelSection title="カスタムドメイン">
            {selected.custom_domain ? (
              <>
                <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 9 }}>
                  <code style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--ink1)', background: 'var(--surface2)', padding: '6px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
                    {selected.custom_domain}
                  </code>
                  <Badge variant={selected.custom_domain_ssl ? 'green' : 'amber'}>
                    {selected.custom_domain_ssl ? 'SSL 有効' : 'SSL 未取得'}
                  </Badge>
                </div>
                {/* SSL証明書詳細情報 */}
                {selected.custom_domain_ssl && (
                  <div className={styles.sslInfoBox}>
                    {certLoading ? (
                      <div style={{ fontSize: 12, color: 'var(--ink3)' }}>証明書情報を取得中...</div>
                    ) : certInfo ? (
                      <>
                        <div className={styles.sslInfoRow}>
                          <span className={styles.sslInfoKey}>発行者</span>
                          <span className={styles.sslInfoVal}>{certInfo.issuer.split('CN=')[1] ?? certInfo.issuer}</span>
                        </div>
                        <div className={styles.sslInfoRow}>
                          <span className={styles.sslInfoKey}>有効期限</span>
                          <span className={styles.sslInfoVal}>{new Date(certInfo.notAfter).toLocaleDateString('ja-JP')}</span>
                        </div>
                        <div className={styles.sslInfoRow}>
                          <span className={styles.sslInfoKey}>残り日数</span>
                          <span className={certInfo.daysRemaining > 30 ? styles.sslDaysValid : certInfo.daysRemaining > 0 ? styles.sslDaysWarn : styles.sslDaysExpired}>
                            {certInfo.daysRemaining}日
                          </span>
                          {certInfo.status !== 'valid' && (
                            <Badge variant={certInfo.status === 'expiring_soon' ? 'amber' : 'red'}>
                              {certInfo.status === 'expiring_soon' ? '期限間近' : '期限切れ'}
                            </Badge>
                          )}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--ink3)' }}>証明書情報を取得できません</div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!selected.custom_domain_ssl && (
                    <button className={styles.btnPrimary} disabled={customDomainLoading} onClick={async () => {
                      setCustomDomainLoading(true);
                      try {
                        await api(`/console/tenants/${selected.id}/custom-domain/ssl`, { method: 'POST' });
                        toast('✓ SSL証明書を取得しました');
                        refetch();
                        setSelected((prev) => prev ? { ...prev, custom_domain_ssl: true } : prev);
                      } catch (e) { toast(e instanceof Error ? e.message : 'SSL取得に失敗しました', 'error'); }
                      finally { setCustomDomainLoading(false); }
                    }}>
                      {customDomainLoading ? '取得中...' : '🔒 SSL証明書を取得'}
                    </button>
                  )}
                  {selected.custom_domain_ssl && isSuperAdmin && (
                    <button className={styles.btnPrimary} disabled={sslRenewing} onClick={async () => {
                      setSslRenewing(true);
                      try {
                        await api(`/console/certificates/${encodeURIComponent(selected.custom_domain!)}/renew`, { method: 'POST' });
                        toast('✓ SSL証明書を更新しました');
                        setCertInfo(prev => prev ? { ...prev, daysRemaining: 90, status: 'valid', notAfter: new Date(Date.now() + 90 * 86_400_000).toISOString() } : prev);
                      } catch (e) { toast(e instanceof Error ? e.message : 'SSL更新に失敗しました', 'error'); }
                      finally { setSslRenewing(false); }
                    }}>
                      {sslRenewing ? '更新中...' : '🔄 SSL更新'}
                    </button>
                  )}
                  <button className={styles.btnDanger} disabled={customDomainLoading} onClick={async () => {
                    setCustomDomainLoading(true);
                    try {
                      await api(`/console/tenants/${selected.id}/custom-domain`, { method: 'DELETE' });
                      toast('カスタムドメインを削除しました');
                      refetch();
                      setSelected((prev) => prev ? { ...prev, custom_domain: null, custom_domain_ssl: false } : prev);
                      setCustomDomainInput('');
                    } catch (e) { toast(e instanceof Error ? e.message : '削除に失敗しました', 'error'); }
                    finally { setCustomDomainLoading(false); }
                  }}>削除</button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 8, fontFamily: 'var(--mono)' }}>
                  DNS: CNAME {selected.custom_domain} → {selected.slug}.jiayin.jp
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 9 }}>
                  <FormInput
                    type="text"
                    value={customDomainInput}
                    onChange={(e) => setCustomDomainInput(e.target.value)}
                    placeholder="app.example.co.jp"
                    style={{ flex: 1, fontFamily: 'var(--mono)' }}
                  />
                  <button className={styles.btnPrimary} disabled={customDomainLoading || !customDomainInput.trim()} onClick={async () => {
                    setCustomDomainLoading(true);
                    try {
                      await api(`/console/tenants/${selected.id}/custom-domain`, {
                        method: 'POST',
                        body: JSON.stringify({ domain: customDomainInput.trim() }),
                      });
                      toast('✓ カスタムドメインを設定しました');
                      refetch();
                      setSelected((prev) => prev ? { ...prev, custom_domain: customDomainInput.trim(), custom_domain_ssl: false } : prev);
                    } catch (e) { toast(e instanceof Error ? e.message : '設定に失敗しました', 'error'); }
                    finally { setCustomDomainLoading(false); }
                  }}>
                    {customDomainLoading ? '設定中...' : '設定'}
                  </button>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ink3)' }}>
                  設定後、DNS に CNAME レコードを追加してください:<br />
                  <code style={{ fontFamily: 'var(--mono)' }}>{selected.slug}.jiayin.jp</code>
                </div>
              </>
            )}
          </PanelSection>
        </>}
      </SlidePanel>

      {/* テナント追加モーダル（旧HTMLモック m-tenant 完全移植） */}
      <Modal open={showAddModal} onClose={() => { setShowAddModal(false); setVerifyStep('form'); setVerifyCode(''); }} title="🏢 テナント新規追加" width={540}
        footer={verifyStep === 'form' ? <>
          <button className={styles.btnGhost} onClick={() => setShowAddModal(false)}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={handleSendVerification} disabled={sending}>{sending ? '送信中...' : 'テナントを作成'}</button>
        </> : <>
          <button className={styles.btnGhost} onClick={() => { setVerifyStep('form'); setVerifyCode(''); }}>戻る</button>
          <button className={styles.btnPrimary} onClick={handleVerifyAndCreate} disabled={sending}>{sending ? '処理中...' : '認証してテナントを作成'}</button>
        </>}
      >
        {verifyStep === 'form' ? <>
        <div className={styles.formSection}>会社情報</div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>会社名 <span style={{ color: 'var(--red)' }}>*</span></label>
          <input className={styles.formInput} type="text" placeholder="例：株式会社〇〇不動産" value={nt.company_name} onChange={(e) => setNt({ ...nt, company_name: e.target.value })} />
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>テナントスラッグ（URL） <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className={styles.formInput} type="text" placeholder="marumaru" style={{ fontFamily: 'var(--mono)' }} value={nt.slug} onChange={(e) => setNt({ ...nt, slug: e.target.value })} />
            <div className={styles.formHint}>{nt.slug || 'marumaru'}.jiayin.jp</div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>宅建業免許番号</label>
            <input className={styles.formInput} type="text" placeholder="東京都知事（1）XXXXXX" value={nt.license_number} onChange={(e) => setNt({ ...nt, license_number: e.target.value })} />
          </div>
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>TEL</label>
            <input className={styles.formInput} type="tel" placeholder={SUPPORT.phone} value={nt.tel} onChange={(e) => setNt({ ...nt, tel: e.target.value })} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>管理者メール <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className={styles.formInput} type="email" placeholder="admin@example.co.jp" required value={nt.admin_email} onChange={(e) => setNt({ ...nt, admin_email: e.target.value })} />
          </div>
        </div>
        <div className={styles.formSection}>プラン・初期設定</div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>契約プラン</label>
            <select className={styles.formSelect} value={nt.plan_id} onChange={(e) => setNt({ ...nt, plan_id: e.target.value })}>
              <option value="2">Standard（¥25,000/月）</option>
              <option value="3">Professional（¥48,000/月）</option>
              <option value="4">Enterprise（¥98,000/月〜）</option>
              <option value="1">{`トライアル（${TRIAL_DAYS}日間無料）`}</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>課金開始日</label>
            <input className={styles.formInput} type="date" value={nt.billing_start} onChange={(e) => setNt({ ...nt, billing_start: e.target.value })} />
          </div>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>初期有効モジュール</label>
          <div className={styles.formChecks}>
            {[
              { id: 'properties', label: '物件管理', required: true },
              { id: 'crm', label: '顧客 CRM' },
              { id: 'contracts', label: '電子契約' },
              { id: 'portals', label: 'ポータル入稿' },
              { id: 'rent_management', label: '家賃管理' },
              { id: 'ai_agent', label: 'AI エージェント' },
            ].map((m) => (
              <label key={m.id} className={styles.formCheck}>
                <input type="checkbox" checked={m.required || nt.modules.includes(m.id)} disabled={m.required} onChange={() => !m.required && toggleModule(m.id)} />
                {m.label}
              </label>
            ))}
          </div>
        </div>
        <div className={styles.infoBanner}>
          ✓ 登録後、<span style={{ fontFamily: 'var(--mono)' }}>tenant_{nt.slug || '{slug}'}</span> スキーマを自動作成し、管理者へ招待メールを送付します。
        </div>
        </> : <>
        {/* 認証コード入力ステップ */}
        <div className={styles.verifySection}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📧</div>
          <div className={styles.verifyText}>
            <span className={styles.verifyEmail}>{nt.admin_email}</span><br />
            に6桁の認証コードを送信しました。
          </div>
          <input
            className={styles.codeInput}
            type="text"
            maxLength={6}
            inputMode="numeric"
            placeholder="000000"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
          />
          <div className={styles.resendRow}>
            コードが届かない場合：{' '}
            <button className={styles.resendLink} onClick={handleResend} disabled={resendCooldown > 0 || sending}>
              {resendCooldown > 0 ? `再送信（${resendCooldown}秒）` : '再送信'}
            </button>
          </div>
        </div>
        </>}
      </Modal>

      {/* トライアル延長モーダル */}
      <Modal
        open={showExtendTrial}
        onClose={() => setShowExtendTrial(false)}
        title="トライアル期間延長"
        width={400}
        footer={
          <>
            <button className={styles.btnGhost} onClick={() => setShowExtendTrial(false)}>キャンセル</button>
            <button className={styles.btnPrimary} onClick={handleExtendTrial} disabled={extending}>
              {extending ? '処理中...' : '延長する'}
            </button>
          </>
        }
      >
        <p style={{ fontSize: 13, marginBottom: 12 }}>
          <strong>{selected?.company_name}</strong> のトライアル期間を延長します。
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            className={styles.formInput}
            type="number"
            min={1}
            max={365}
            value={extendDays}
            onChange={e => setExtendDays(e.target.value)}
            style={{ width: 80 }}
          />
          <span style={{ fontSize: 13, color: 'var(--ink2)' }}>日間延長</span>
        </div>
        {selected?.status === 'suspended' && (
          <p style={{ fontSize: 11.5, color: 'var(--amber, #f59e0b)', marginTop: 8 }}>
            ※ 停止中のテナントはトライアル状態に復帰します。
          </p>
        )}
      </Modal>
    </div>
  );
}
